import find from 'lodash/find';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, {Component, useState, useRef, useCallback, useEffect} from 'react';
import { connect } from '~/redux/connect';
import cn from 'classnames';

import {QrScanner} from '~/ui/QrScanner/QrScanner';
import { ActionMenuMessageList } from './ActionMenuMessageList';
import {
    getAllResultParams,
    getCustomActionResult,
    getGlobalActions,
    getGroups,
    getModelName,
    getModelTitle,
    getModelTooltip,
    getResultsValue,
    getSelectThroughout,
    getSelectedRows,
    getPageAction,
} from "~/stateGetter";
import {presentActionInfo} from "~/statePresenter";
import {performCustomAction, performCustomScanQrAction} from "~/rx/model/customAction";
import stripTags from 'striptags';
import ItemGrid from '~/ui/ItemGrid';
import identify from "~/identify";
import css from "./css.css";
import groupBy from 'lodash/groupBy';
import memoize from "lodash/memoize";

import { SpinnerButton as Button, SaveButton, Dialog, Divider, HStack, IconMenu, Icon, MenuItem, Text, Tooltip, VStack, ScrollView } from "~/native-ui";
import { ConfirmField } from './ConfirmField';



const ActionItem = (action) => {
    return (<MenuItem
        icon={action.icon}
        iconFamily={action.iconFamily}
        iconColor={action.iconColor}
        caption={action.caption}
        value={action.value}
        onClick={action.onClick}
        disabled={!action.allowDirty && action.disabled}
        mx={action.mx}
        tooltip={action.description}
    />)
};


const ActionButtonTypes = {
    SaveButton,
    Button
}

const ActionButton = (props) => {
    const ButtonType = ActionButtonTypes[props.buttonType] ?? ActionButtonTypes.Button;
    const icon = props.icon,
          iconFamily = props.iconFamily,
          caption = props.caption,
          variant = props.variant,
          description = props.description || caption,
          allowDirty = props.allowDirty,
          label = props.showLabel || !icon ? caption : '',
          button = (
                <ButtonType
                    primary
                    iconSize="sm"
                    key={props.value}
                    icon={icon}
                    iconFamily={iconFamily}
                    label={label}
                    onPress={props.onClick}
                    variant={!allowDirty && props.disabled ? undefined : variant}
                    disabled={!allowDirty && props.disabled}
                />
            );

    const addTooltip = !!props.description || !props.showLabel;

    if (!icon || !addTooltip) {
        return button;
    }
    
    return (
        <Tooltip tooltip={description} disabled={props.disabled} >{ button }</Tooltip>
    )
};

/**
 * @typedef ActionsObjectGroup
 * @type {object}
 * 
 * @property {string} icon
 * @property {string} [iconFamily]
 * @property {string} name
 * @property {number} order
 * @property {string} title
 */

/**
 * @typedef ActionsObject
 * @type {object}
 * @property {function} action
 * @property {ActionsObjectGroup} group
 * @property {boolean} isActive
 * @property {boolean} [requiresSelection]
 * @property {number} [criticalConfirmQuantity]
 * @property {string} [criticalConfirmText]
 * @property {boolean} disabled
 * 
 * @property {string} caption
 * @property {string} [captionActive]
 * @property {string} [dialogTitle]
 * @property {string} [grid]
 * @property {boolean} hasPermission
 * @property {string} [icon]
 * @property {string} [iconFamily]
 * @property {boolean} [noReload]
 * @property {boolean} [scanQr]
 * @property {boolean} [tooltip]
 * @property {string} value
 */



/**
 * Action Menu Component
 *
 * @type {React.ComponentClass<{ actions: ActionsObject[]}>} 
 * @returns {React.ReactElement} ActionsMenu
 */
export class ActionMenu extends Component {
    static propTypes = {
        actions: PropTypes.object,
        activeAction: PropTypes.object,
        isActionActive: PropTypes.bool.isRequired,
        title: PropTypes.string,
        disabled: PropTypes.bool.isRequired,
        dropdownMode: PropTypes.bool,
        showStartDivider: PropTypes.bool,
        standAlonePrimaryAction: PropTypes.bool,
    };

    static defaultProps = {
        isActionActive: false,
        disabled: false
    };

    state  = {
        confirmAction: null,
        showDialog: false,
        criticalConfirmValid: false,
    };

    get disablePrimaryConfirm () {
        return !this.state.criticalConfirmValid;
    }

    componentDidMount() {
        this.autoOpenModel()
    }

    componentDidUpdate(prevProps) {
        if (this.props.modelAction !== prevProps.modelAction) {
            this.autoOpenModel()
        }
    }

    findAction(predicate) {
        return find(Object.values(this.props.actions).flat(), predicate);
    }

    autoOpenModel = () => {
        const { modelAction } = this.props;
        if (modelAction?.action) {
            const action = this.findAction({value: modelAction.action})
            this.performAction(action);
        }
    }

    hideConfirmActionDialog = () => {
        this.props.onCloseAction?.();
        this.setState({showDialog: false});
    };

    setActionDialog = (actionName, criticalConfirm) => {
        this.setState({ confirmAction: actionName, showDialog: true, criticalConfirmValid: !criticalConfirm });
    };


    performAction = async (action) => {
        if (!action) {
            return;
        }

        const shouldConfirmAction = action.confirmation === true
            || action.requiresSelection && action.confirmation <= this.props.selectedRowCount
            || action.criticalConfirmText
            || action.grid
            || action.scanQr
            || this.props.modelAction?.action === action.value;

        if (shouldConfirmAction) {
            return this.setActionDialog(action.value);
        }

        action.action(action.value);
    }

    onChangeCriticalConfirm = (isMatch) => {
        this.setState({ criticalConfirmValid: isMatch });
    }

    prepareAction = (theActions, action) => {
        if(action.disabled && !action.allowDisabled) { return theActions; }
        

        theActions.push({
            ...action,
            onClick: async () => this.performAction(action)
        });

        return theActions;
    };
    
    renderDialog = () => {
        let actionName = this.state.confirmAction;
        if(!actionName || !actionName.length) {
            return null;
        }
            
        let action = this.findAction({value: actionName});
        
        if(action.scanQr) {
            return this.renderQrCodeDialog(action);
        }

        if(action.grid) {
            return this.renderActionGrid(action);
        }

        if(action.requiresSelection) {
            return this.renderActionRequiresSelectionDialog(action);    
        }
        
        return this.renderActionConfirmDialog(action);
    };

    renderBaseDialog = (children, { translate = {}, showConfirmActions = true, showCancel = true, showConfirm = true, dialogProps = {} } = {}) => {
        let { trans, title, isActionActive } = this.props;

        let confirmedActionName = this.state.confirmAction,
            showDialog = this.state.showDialog,
            confirmedAction = this.findAction({value: confirmedActionName}),
            actionTitle = get(confirmedAction, 'caption');

        let transData = {
            actionTitle,
            itemTitle: stripTags(title),
        };

        let confirmTitle = confirmedAction.dialogTitle
            ? confirmedAction.dialogTitle
            : trans('confirmAction.title', transData);

        const confirmActions =  !showConfirmActions ? null : [
            {
                label: translate.okay ?? actionTitle,
                isLoading: isActionActive,
                _stack: isActionActive ? {space: "1"} : undefined,
                disabled: this.disablePrimaryConfirm || isActionActive,
                onClick: async () => {
                    const resultPromise = confirmedAction.action(confirmedActionName, confirmedAction.grid, confirmedAction.tags);

                    const shouldClose = confirmedAction.closeOn !== 'success';
                    if (shouldClose) return this.hideConfirmActionDialog();

                    const result = await resultPromise;
                    if (result && "success" in result && result.success !== true) return;

                    this.hideConfirmActionDialog();
                }
            },
            !showCancel ? null : {
                label: translate.no ?? trans('no'),
                onClick: this.hideConfirmActionDialog,
            }
        ].filter(Boolean);

        return (
            <Dialog
                key="dialog"
                className={css.dialog}
                actions={confirmActions}
                active={showDialog}
                title={confirmTitle}
                onOverlayClick={this.hideConfirmActionDialog}
                onEscKeyDown={this.hideConfirmActionDialog}
                {...dialogProps}
            >
                {children}
            </Dialog>
        )
    };

    renderQrCodeDialog(confirmedAction) {
        const scannerProps = {
            modelId: this.props.modelId,
            onScan: async (qr_code_string) => {
                if (await confirmedAction.action(this.state.confirmAction, { qr_code_string }, confirmedAction.grid)) {
                    this.hideConfirmActionDialog()
                }
            }
        }

        return this.renderBaseDialog(<QrScanner { ...scannerProps} />, { showConfirm: false })
    }

    renderActionGrid = (confirmedAction) => {
        let requiresSelection = confirmedAction.requiresSelection,
            { trans, rowCount, modelTitle } = this.props;

        return this.renderBaseDialog(
            <React.Fragment>
                <ActionMenuMessageList
                    modelId={this.props.modelId}
                    requiresSelection={requiresSelection}
                    modelTitle={modelTitle}
                    rowCount={rowCount}
                    actionName={this.state.confirmAction}
                />
                <ItemGrid
                    modelId={this.props.modelId}
                    gridName={confirmedAction.grid}
                    view={identify.VIEW_EDIT}
                    className={css.itemGrid}
                />
            </React.Fragment>,
            {
                dialogProps: {
                    type: "medium-auto"
                }
            }
        );
    };

    renderConfirmField (action) {
        const { criticalConfirmText, criticalConfirmQuantity = 0 } = action;
        if (!criticalConfirmText) {
            return null;
        }

        return (
            <ConfirmField
                modelId={this.props.modelId}
                threshold={criticalConfirmQuantity || 0}
                onChange={this.onChangeCriticalConfirm}
                valueToConfirm={action.criticalConfirmText}
            />
        )
    }

    renderActionRequiresSelectionDialog = (action) => {
        let { trans, title, rowCount, modelTitle } = this.props;

        const actionProps = {
            count: rowCount,
            modelNamePlural: modelTitle
        }

        let transData = {
                actionTitle: trans('requiresSelection.title', actionProps),
                itemTitle: stripTags(title),
                ...actionProps,
            },
            confirmMessage = trans('requiresSelection.message', transData)

        return this.renderBaseDialog((
            <VStack space="2">
                <Text>{confirmMessage}</Text>
                {this.renderConfirmField(action)}
            </VStack>
        ), {
            translate: {
                okay: transData.actionTitle
            }
        })
    };



    renderActionConfirmDialog = (action) => {
        let { trans, title } = this.props;

        let confirmedActionName = this.state.confirmAction,
            confirmedAction = this.findAction({value: confirmedActionName}),
            actionTitle = get(confirmedAction, 'caption');

        let transData = {
            actionTitle,
            itemTitle: stripTags(title)
        };

        let confirmMessage = trans(get(confirmedAction, 'confirmationMessage', 'confirmAction.message'), transData);

        return this.renderBaseDialog((
            <VStack space="2" className="test">
                <Text>{confirmMessage}</Text>
                {this.renderConfirmField(action)}
            </VStack>
        ))
    };


    getButtonProps = memoize((disabled) => ({ disabled: disabled, primary: true, iconSize: "sm" }))

    prepareMenuGroup = ([group, groupActions]) => {                         
        groupActions = groupActions.reduce(this.prepareAction, []);

        if (groupActions.length <= 0) {
            return null
        }

        let { activeGroup, activeAction, isActionActive, className, menuClassName, disabledClassName, } = this.props,
            iconClassName = cn(menuClassName, {[disabledClassName]: isActionActive}),
            isActiveGroup = !!isActionActive && activeGroup?.name === group,
            actionGroup = get(groupActions, "0.group", {}),
            groupIcon = {
                icon: actionGroup.icon ?? 'more-vert',
                iconFamily: actionGroup.icon ? actionGroup.iconFamily : undefined,
            },
            icon = isActiveGroup ? 'spinning' : (groupIcon.icon ?? 'more-vert'),
            iconFamily = !isActiveGroup ? groupIcon.iconFamily : undefined;


        const {showLabel, description} = get(groupActions, "0.group", {});
        const title = get(groupActions, "0.group.title", group);
        const tooltip = description || (!showLabel ? title : undefined);

        return {
            "aria-description": tooltip,
            tooltip: tooltip,
            key: title,
            icon: icon,
            name: actionGroup.name || 'empty',
            iconFamily: iconFamily,
            iconClassName:iconClassName,
            buttonProps: this.getButtonProps(this.props.disabled),
            actions: groupActions,
            label: showLabel ? title : undefined,
            subgroup: actionGroup.subgroup,
            shouldFlatten: actionGroup.flatten
        }
    }


    renderMenu () {
        let { actions, standAlonePrimaryAction = true } = this.props;

        const menuGroups = Object.entries(actions).map(this.prepareMenuGroup)
        if (menuGroups.length < 1) {
            return null;
        }


        const MenuComponent = this.props.dropdownMode ? DropdownMenu : FlatMenu;
        return <MenuComponent
            menuGroups={menuGroups}
            standAlonePrimaryAction={standAlonePrimaryAction}
            showStartDivider={this.props.showStartDivider}
            buttonProps={this.getButtonProps(this.props.disabled)}
        />;
    }

    render() {
        let { actions, className, } = this.props;

        return (
            <React.Fragment>
                {this.renderDialog()}
                <React.Fragment key={'menu'}>{Object.keys(actions).length ? this.renderMenu() : null}</React.Fragment>
            </React.Fragment>
        );
    }
}


/**
 * @typedef MenuGroups
 * @type {object}
 * 
 * @property {ActionsObject[]} defaultGroups.actions
 */



/**
 * @typedef MenuProps
 * @type {object}
 * 
 * @property {MenuGroups[]} [actionElements]
 * @property {Array} flatActions
 * @property {(MenuGroups | null)[]} [defaultGroups]
 */


const renderAction  = (actionProps) => {
    return <ActionItem {...actionProps} key={actionProps.value ?? actionProps.value ?? index + "$"} />;
}

const prepareSubgroupActions = (actions, key = '', extraProps = () => {}) => {
    return Object.values(groupBy(actions, "subgroup.name")).map((subgroupActions, index, self) => {
        const subgroupElements = subgroupActions.map((actionProps) => renderAction({...actionProps, ...extraProps(actionProps)}));
        if (self.at(-1) === subgroupActions) return subgroupElements;

        return [
            subgroupElements,
            <Divider key={key + index + "divider"} orientation="horizontal" bg="coolGray.300:alpha.30" h="1px" mx="8px" width="calc(100% - 16px)" />
        ]
    }).flat()
}


/**
 * Dropdown Action Menu Component
 * 
 *
 * @type {React.FC<MenuProps>} 
 * @returns {React.ReactElement} ActionsMenu
 */
const DropdownMenu = (props) => {
    const { standAlonePrimaryAction, buttonProps } = props;
    const menuGroups = props.menuGroups.filter(Boolean);
    const primaryMenu = menuGroups.filter((itemProps) => itemProps.actions[0].isPrimary);
    const secondaryMenu = menuGroups.filter((itemProps) => !itemProps.actions[0].isPrimary);

    const menu = standAlonePrimaryAction ? secondaryMenu : [...primaryMenu, ...secondaryMenu];

    const allActions = menu.reduce((flattenedActions, itemProps, index, self) => {
            return flattenedActions.concat(
                prepareSubgroupActions(itemProps.actions, index + "_", (action) => action?.isPrimary ? ({ iconColor: "primary.500", mx: "2", }) : { icon: undefined }),
                index === self.length - 1 ? null : <Divider key={index} orientation="horizontal" bg="coolGray.300" h="1px" mx="8px" width="calc(100% - 16px)" />
            );
        }, []).flat();

    const item = allActions.length === 1 ? allActions : 
            <IconMenu
                icon={"more-vert"}
                buttonProps={buttonProps}
                // buttonProps={this.getButtonProps(this.props.disabled)}
            >
                {allActions}
            </IconMenu>

    if (standAlonePrimaryAction === false) {
        return <HStack>{item}</HStack>
    }

    const shouldShowDivider = !!primaryMenu.length && primaryMenu.some(x => x.actions.length);
    const divider = shouldShowDivider ? <Divider orientation="vertical" bg="coolGray.300" h="5" /> : null;

    return (
        <HStack space={divider ? 2 : 0} alignItems="center" key="actions">
            {item}
            {divider}
            {!!primaryMenu.length && <FlatMenu {...props} menuGroups={primaryMenu} showLabel={false} />}
        </HStack>
    )
}



/**
 * Flat Action Menu Component
 * 
 *
 * @type {React.FC<MenuProps>} 
 * @returns {React.ReactElement} ActionsMenu
 */
const FlatMenu = (props) => {
    const scrollViewRef = useRef();
    const [showLabel, setShowLabel] = useState(props.showLabel ?? true);
    const dividerElement = (key, dividerProps = {}) => <Divider orientation="vertical" bg="coolGray.300" h="5" key={key} {...dividerProps} />;
    const divider = !!props.showStartDivider && dividerElement("divider", { mr: "2" });

    const allActions = props.menuGroups
        .reduce((flattenedActions, itemProps, index, self) => {
            if (!itemProps) return flattenedActions;
            
            const { actions, shouldFlatten, ...menuProps } = itemProps;
            const divider = (flattenedActions.length && itemProps?.subgroup !== self[index-1]?.subgroup) ?
                dividerElement(itemProps.name + "_divider")
                : null;

            if (shouldFlatten)
                return flattenedActions.concat(divider, actions.map((action) => <ActionButton key={action.value ?? action.caption} {...action} showLabel={showLabel && action.showLabel} />));


            return flattenedActions.concat(
                divider,
                <IconMenu {...menuProps} showLabel={showLabel && menuProps.showLabel} key={itemProps.name}>
                    {prepareSubgroupActions(actions, index + "_", (action) => action.isPrimary ? {} : { icon: undefined })}
                </IconMenu>
            )
        }
        
        , []).filter(Boolean);

    const handleLayoutChanged = useCallback(() => {
        if (!scrollViewRef.current) return;
        const {scrollWidth, clientWidth} = scrollViewRef.current;
        if (scrollWidth > clientWidth) setShowLabel(false);
    }, []);


    useEffect(() => {
        if (typeof ResizeObserver === "undefined") {
            handleLayoutChanged();
            return;
        }

        const resizeObserver = new ResizeObserver((mutation) => {
            handleLayoutChanged();
        });
        const node = scrollViewRef.current
        resizeObserver.observe(node, { "border-box": true, });

        return () => {
            resizeObserver.disconnect();
        }
    }, [handleLayoutChanged]);


    return (
        <React.Fragment>
            {divider}
            <ScrollView key="actions" horizontal ref={scrollViewRef} onLayout={handleLayoutChanged} style={{flexGrow: 0}}>
                <HStack space={2} alignItems="center" flexDirection="row-reverse">{allActions}</HStack>
            </ScrollView>
        </React.Fragment>
    )
}


/**
 * Action Menu Component
 *
 * @type {React.ComponentClass<{ modelId: string, actions?: ActionsObject[], dropdownMode?: boolean }>} 
 * @returns {React.ReactElement} ActionsMenu
 */
export const TableActionMenu = connect((state, {modelId}) => {
    return {
        modelAction: getPageAction(state, modelId),
        modelName: getModelName(state, modelId),
        modelTitle: getModelTitle(state, modelId),
        actions: getGlobalActions(state, modelId),
        rowCount: getResultsValue(state, modelId).total,
        customActionResult: getCustomActionResult(state, modelId),
        tooltip:  getModelTooltip(state, modelId, 'global_actions'),
        selectedThroughout: getSelectThroughout(state, modelId),
        selectedRows: getSelectedRows(state, modelId),
        groups: getGroups(state, modelId),
        ...getAllResultParams(state, modelId),
    }
}, (dispatch, {modelId}) => {
    return {
        executeAction: (actionName, gridName, ...tags) => dispatch(performCustomAction(modelId, actionName, undefined, gridName, tags)),
        executeScanQrAction: (actionName, data, gridName) => dispatch(performCustomScanQrAction(modelId, actionName, data, gridName)),
    }
}, ({groups, actions, customActionResult, tooltip, selectedThroughout, selectedRows, rowCount, modelTitle, modelAction}, {executeAction, executeScanQrAction}, {...otherOwnProps}) => {
    const actionInfo = presentActionInfo({
        actions: [...actions, ...otherOwnProps.actions ?? []], 
        groups, executeAction, executeScanQrAction, actionResult: customActionResult,
        selectedRows: !selectedThroughout ? selectedRows : [true]
    });
    return {
        tooltip,
        rowCount,
        modelTitle,
        modelAction,
        selectedRowCount: selectedRows.length,
        ...otherOwnProps,
        ...actionInfo,
    }
})(ActionMenu);